import { RequestError, isUnauthorized } from '#errors/index.ts';
import { createStore } from '#utils/store/index.ts';
import {
  AuthError,
  getCallbackCode,
  hasSession,
  isBusy,
  toAuthFailure,
  toCallbackPayload,
  toProviderFailure,
} from './auth.helpers.ts';
import type {
  TAuth,
  TAuthError,
  TAuthOptions,
  TAuthState,
  TMagicAuthCallback,
  TSessionResult,
  TSignInCallback,
  TSignInResult,
} from './auth.types.ts';

export const DEFAULT_RESEND_DELAY = 120_000;

export const createAuth = ({
  api,
  platform,
  magicAuthEmail: initialMagicAuthEmail,
  resendDelay = DEFAULT_RESEND_DELAY,
  authenticate = async (payload) => {
    const response = await api.auth.loginCallback(payload);

    if (!response.success) {
      throw new RequestError({ kind: 'decode', retryable: false });
    }
  },
  onMagicAuthEmailChange,
}: TAuthOptions): TAuth => {
  let pendingMagicAuth: TMagicAuthCallback | undefined;
  let magicAuthEmail = initialMagicAuthEmail;
  let hasRestored = false;
  let restoredCallbackCode: string | undefined;

  const { getState, setState, subscribe } = createStore<TAuthState>({
    status: 'restoring',
  });

  const updateMagicAuthEmail = (email?: string): void => {
    magicAuthEmail = email;
    onMagicAuthEmailChange?.(email);
  };

  const isRefreshing = (): boolean => getState().status === 'refreshing';

  const throwIfBusy = (): void => {
    const state = getState();

    if (!isBusy(state)) {
      return;
    }

    throw new AuthError('busy');
  };

  const loadSession = (): Promise<TSessionResult> =>
    api.user.getUserData({}).then(
      (user) => ({ user }),
      (error: unknown) => ({ error }),
    );

  const exchangeCallback = async (callback: TSignInCallback): Promise<void> => {
    pendingMagicAuth = undefined;

    if (!('code' in callback) && !magicAuthEmail) {
      pendingMagicAuth = callback;

      throw new AuthError('missing_email');
    }

    const payload = toCallbackPayload(callback, magicAuthEmail);

    await authenticate(payload);
  };

  const tryExchange = async (
    exchange: () => Promise<void>,
    isDemo?: boolean,
  ): Promise<TAuthError | undefined> => {
    const exchangeFailure = await exchange().then(
      () => undefined,
      toAuthFailure,
    );

    const session = await loadSession();

    if (!session.user) {
      return exchangeFailure ?? toAuthFailure(session.error);
    }

    const isDemoSession = isDemo && !exchangeFailure;

    updateMagicAuthEmail(undefined);
    setState({
      status: 'authenticated',
      user: session.user,
      isDemo: isDemoSession,
    });

    return undefined;
  };

  const signInWithExchange = async (
    exchange: () => Promise<void>,
    isDemo?: boolean,
  ): Promise<void> => {
    setState({ status: 'signing-in' });

    const failure = await tryExchange(exchange, isDemo);

    if (!failure) {
      return;
    }

    setState({ status: 'anonymous', reason: 'failed', error: failure });
  };

  const restore = async (callback?: TSignInCallback): Promise<void> => {
    const callbackCode = getCallbackCode(callback);

    const isRestored =
      hasRestored &&
      (callbackCode === undefined || callbackCode === restoredCallbackCode);

    if (isRestored) {
      return;
    }

    if (getState().status !== 'restoring') {
      throwIfBusy();
    }

    hasRestored = true;
    restoredCallbackCode = callbackCode;

    if (callback) {
      return signInWithExchange(() => exchangeCallback(callback));
    }

    const session = await loadSession();

    if (!session.user) {
      const isSessionMissing = isUnauthorized(session.error);

      setState(
        isSessionMissing
          ? { status: 'anonymous' }
          : { status: 'anonymous', error: toAuthFailure(session.error) },
      );

      return;
    }

    updateMagicAuthEmail(undefined);
    setState({ status: 'authenticated', user: session.user });
  };

  const refresh = async (): Promise<void> => {
    throwIfBusy();

    const current = getState();

    if (current.status !== 'authenticated') {
      return;
    }

    setState({ ...current, status: 'refreshing' });

    const session = await loadSession();

    if (!isRefreshing()) {
      return;
    }

    if (session.user) {
      setState({ ...current, user: session.user });

      return;
    }

    if (isUnauthorized(session.error)) {
      setState({ status: 'anonymous', reason: 'expired' });

      return;
    }

    setState(current);

    throw toAuthFailure(session.error);
  };

  const signIn = async (email: string): Promise<TSignInResult> => {
    throwIfBusy();
    setState({ status: 'signing-in' });

    const pendingLink = pendingMagicAuth;

    pendingMagicAuth = undefined;

    if (pendingLink) {
      const payload = toCallbackPayload(pendingLink, email);
      const failure = await tryExchange(() => authenticate(payload));

      if (!failure) {
        return { kind: 'completed' };
      }
    }

    try {
      const { oidc } = await api.auth.getEmailOidc({ email });

      if (oidc !== 'workos') {
        throw new AuthError('unsupported_domain');
      }

      const login = await api.auth.login({ email, platform });

      if (login.sign_in_method === 'email') {
        const magicAuth = { email, canResendAt: Date.now() + resendDelay };

        updateMagicAuthEmail(email);
        setState({ status: 'signing-in', magicAuth });

        return { kind: 'magic-auth', magicAuth };
      }

      if (login.authorization_url) {
        return { kind: 'redirect', url: login.authorization_url };
      }

      throw new RequestError({ kind: 'decode', retryable: false });
    } catch (error) {
      const failure = toAuthFailure(error);

      setState({ status: 'anonymous', reason: 'failed', error: failure });

      return { kind: 'failed', error: failure };
    }
  };

  const signInWithProvider = async (oidc: string): Promise<TSignInResult> => {
    throwIfBusy();
    setState({ status: 'signing-in' });

    try {
      const login = await api.auth.loginWithOidc({ oidc, platform });

      if (login.auth_system !== 'workos') {
        throw new AuthError('unsupported_provider');
      }

      if (!login.redirect_uri) {
        throw new RequestError({ kind: 'decode', retryable: false });
      }

      return { kind: 'redirect', url: login.redirect_uri };
    } catch (error) {
      const failure = toProviderFailure(error);

      setState({ status: 'anonymous', reason: 'failed', error: failure });

      return { kind: 'failed', error: failure };
    }
  };

  const signInAsDemo = async (): Promise<void> => {
    throwIfBusy();

    const exchangeDemo = async () => {
      const demo = await api.auth.loginAsDemoUser();

      await authenticate({
        magic_auth_code: demo.magic_auth_code,
        email: demo.email,
      });
    };

    return signInWithExchange(exchangeDemo, true);
  };

  const signOut = async (): Promise<void> => {
    const state = getState();

    if (isBusy(state) && !isRefreshing()) {
      return;
    }

    setState({ status: 'signing-out', user: state.user });

    pendingMagicAuth = undefined;
    updateMagicAuthEmail(undefined);

    const failure = await api.auth
      .logout()
      .then(() => undefined, toAuthFailure);

    setState(
      failure
        ? { status: 'anonymous', reason: 'signedOut', error: failure }
        : { status: 'anonymous', reason: 'signedOut' },
    );
  };

  const expire = (): void => {
    const state = getState();

    if (!hasSession(state)) {
      return;
    }

    pendingMagicAuth = undefined;
    setState({ status: 'anonymous', reason: 'expired' });
  };

  return {
    get state() {
      return getState();
    },
    subscribe,
    restore,
    refresh,
    signIn,
    signInWithProvider,
    signInAsDemo,
    signOut,
    expire,
  };
};
